import { WILAYAH, WILAYAH_ALIAS } from "@/util/wilayahData";

/**
 * Deteksi provinsi & kabupaten/kota dari alamat teks bebas.
 *
 * Alamat di Firestore hanya berupa string (hasil Google Maps / geocoder
 * perangkat, atau ketikan pengguna), jadi lokasinya dikenali dengan mencocokkan
 * nama wilayah resmi. Contoh yang dikenali:
 *   "Jl. Raya No.1, Kec. Sukodono, Kabupaten Sidoarjo, Jawa Timur 61258, Indonesia"
 *   "desa sumberejo kec pagak kab. malang jatim"
 *   "South Jakarta City, Jakarta"
 */

export interface Lokasi {
  province: string | null;
  /** Label kab/kota, mis. "Kab. Sidoarjo" atau "Kota Surabaya". */
  city: string | null;
}

type CityType = "kab" | "kota";

interface CityEntry {
  type: CityType;
  province: string;
  label: string;
}

export const PROVINCES = Object.keys(WILAYAH);

const PROVINCE_ALIASES: Record<string, string[]> = {
  Aceh: ["nanggroe aceh darussalam", "daerah istimewa aceh", "nad"],
  "Sumatera Utara": ["sumatra utara", "north sumatra", "sumut"],
  "Sumatera Barat": ["sumatra barat", "west sumatra", "sumbar"],
  "Kepulauan Riau": ["riau islands", "kepri"],
  "Sumatera Selatan": ["sumatra selatan", "south sumatra", "sumsel"],
  "Kepulauan Bangka Belitung": [
    "bangka belitung",
    "bangka belitung islands",
    "babel",
  ],
  "DKI Jakarta": [
    "daerah khusus ibukota jakarta",
    "daerah khusus jakarta",
    "special capital region of jakarta",
    "jakarta",
  ],
  "Jawa Barat": ["west java", "jabar"],
  "Jawa Tengah": ["central java", "jateng"],
  "DI Yogyakarta": [
    "daerah istimewa yogyakarta",
    "special region of yogyakarta",
    "yogyakarta",
    "jogjakarta",
    "jogja",
    "diy",
  ],
  "Jawa Timur": ["east java", "jatim"],
  "Nusa Tenggara Barat": ["west nusa tenggara", "ntb"],
  "Nusa Tenggara Timur": ["east nusa tenggara", "ntt"],
  "Kalimantan Barat": ["west kalimantan", "kalbar"],
  "Kalimantan Tengah": ["central kalimantan", "kalteng"],
  "Kalimantan Selatan": ["south kalimantan", "kalsel"],
  "Kalimantan Timur": ["east kalimantan", "kaltim"],
  "Kalimantan Utara": ["north kalimantan", "kaltara"],
  "Sulawesi Utara": ["north sulawesi", "sulut"],
  "Sulawesi Tengah": ["central sulawesi", "sulteng"],
  "Sulawesi Barat": ["west sulawesi", "sulbar"],
  "Sulawesi Selatan": ["south sulawesi", "sulsel"],
  "Sulawesi Tenggara": ["southeast sulawesi", "sultra"],
  "Maluku Utara": ["north maluku", "malut"],
  "Papua Barat": ["west papua"],
  "Papua Barat Daya": ["southwest papua"],
  "Papua Selatan": ["south papua"],
  "Papua Tengah": ["central papua"],
  "Papua Pegunungan": ["highland papua", "papua highlands"],
};

const EN_DIRECTIONS: Record<string, string> = {
  north: "utara",
  south: "selatan",
  east: "timur",
  west: "barat",
  central: "tengah",
  southeast: "tenggara",
  southwest: "barat daya",
};

// Segmen yang jelas bukan nama kab/kota (desa, kecamatan, jalan, ...)
const NON_CITY_PREFIXES = new Set([
  "jl",
  "jln",
  "jalan",
  "gg",
  "gang",
  "desa",
  "ds",
  "dusun",
  "dsn",
  "kel",
  "kelurahan",
  "kec",
  "kecamatan",
  "rt",
  "rw",
  "no",
  "blok",
  "perum",
  "perumahan",
]);

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9,]+/g, " ")
    .replace(/\s*,\s*/g, ",")
    .replace(/\s+/g, " ")
    .trim();
}

function compact(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, "");
}

// ---- Index ------------------------------------------------------------------

const provinceByAlias = new Map<string, string>();
PROVINCES.forEach((province) => {
  provinceByAlias.set(normalize(province), province);
  (PROVINCE_ALIASES[province] ?? []).forEach((alias) =>
    provinceByAlias.set(alias, province)
  );
});
// Alias terpanjang dicek dulu agar "papua barat daya" tidak terbaca "papua".
const provinceAliasesByLength = Array.from(provinceByAlias.keys()).sort(
  (a, b) => b.length - a.length
);

const cityByKey = new Map<string, CityEntry>(); // "kab|sidoarjo"
const citiesByBareName = new Map<string, CityEntry[]>(); // "malang" -> [kab, kota]

function addBare(name: string, entry: CityEntry) {
  const key = compact(name);
  const list = citiesByBareName.get(key) ?? [];
  if (!list.includes(entry)) list.push(entry);
  citiesByBareName.set(key, list);
}

PROVINCES.forEach((province) => {
  (["kab", "kota"] as CityType[]).forEach((type) => {
    WILAYAH[province][type].forEach((name) => {
      const entry: CityEntry = {
        type,
        province,
        label: `${type === "kab" ? "Kab." : "Kota"} ${name}`,
      };
      cityByKey.set(`${type}|${compact(name)}`, entry);
      addBare(name, entry);
    });
  });
});

WILAYAH_ALIAS.forEach(({ type, alias, name, bare }) => {
  const entry = cityByKey.get(`${type}|${compact(name)}`);
  if (!entry) return;
  cityByKey.set(`${type}|${compact(alias)}`, entry);
  if (bare) addBare(alias, entry);
});

function lookupCity(type: CityType, words: string[]): CityEntry | undefined {
  const key = compact(words.join(""));
  return (
    cityByKey.get(`${type}|${key}`) ??
    // "Kepulauan X" vs "X" sering tertukar
    (key.startsWith("kepulauan")
      ? cityByKey.get(`${type}|${key.slice("kepulauan".length)}`)
      : cityByKey.get(`${type}|kepulauan${key}`))
  );
}

function englishToIndonesian(words: string[]): string[] {
  let result = [...words];
  let suffix: string[] = [];
  let prefix: string[] = [];
  if (result.length > 1 && EN_DIRECTIONS[result[0]]) {
    suffix = EN_DIRECTIONS[result[0]].split(" ");
    result = result.slice(1);
  }
  if (result.length > 1 && result[result.length - 1] === "islands") {
    prefix = ["kepulauan"];
    result = result.slice(0, -1);
  }
  return [...prefix, ...result, ...suffix];
}

// ---- Deteksi ------------------------------------------------------------------

function segmentsOf(text: string): string[] {
  return text
    .split(",")
    .map((segment) => segment.replace(/\b\d+\b/g, " ").trim())
    .map((segment) => segment.replace(/\s+/g, " "))
    .filter((segment) => segment !== "" && segment !== "indonesia");
}

function detectProvince(segments: string[]): {
  province: string | null;
  strong: boolean;
} {
  // 1. Segmen yang persis nama provinsi (biasanya di akhir alamat)
  for (let i = segments.length - 1; i >= 0; i--) {
    const segment = segments[i].replace(/^(provinsi|propinsi|prov) /, "");
    const province = provinceByAlias.get(segment);
    if (province) return { province, strong: true };
  }
  // 2. Nama provinsi di tengah teks bebas
  const padded = ` ${segments.join(" , ")} `;
  for (const alias of provinceAliasesByLength) {
    if (padded.includes(` ${alias} `)) {
      return { province: provinceByAlias.get(alias) ?? null, strong: false };
    }
  }
  return { province: null, strong: false };
}

type CityMatch =
  | { kind: "exact"; entry: CityEntry; viaKeyword: boolean }
  | { kind: "ambiguous"; candidates: CityEntry[] };

function matchByKeyword(segment: string): CityEntry | undefined {
  const words = segment.split(" ");
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (word === "kabupaten" || word === "kab") {
      for (let n = Math.min(4, words.length - i - 1); n >= 1; n--) {
        const entry = lookupCity("kab", words.slice(i + 1, i + 1 + n));
        if (entry) return entry;
      }
    } else if (word === "kota" || word === "kotamadya" || word === "kodya") {
      let start = i + 1;
      if (words[start] === "administrasi" || words[start] === "adm") start++;
      for (let n = Math.min(4, words.length - start); n >= 1; n--) {
        const entry = lookupCity("kota", words.slice(start, start + n));
        if (entry) return entry;
      }
    } else if (word === "regency" || word === "city") {
      const type: CityType = word === "regency" ? "kab" : "kota";
      for (let n = Math.min(4, i); n >= 1; n--) {
        const entry = lookupCity(
          type,
          englishToIndonesian(words.slice(i - n, i))
        );
        if (entry) return entry;
      }
    }
  }
  return undefined;
}

function detectCity(segments: string[]): CityMatch | null {
  for (let i = segments.length - 1; i >= 0; i--) {
    const entry = matchByKeyword(segments[i]);
    if (entry) return { kind: "exact", entry, viaKeyword: true };
  }

  // Tanpa kata "Kab."/"Kota": cocokkan nama di akhir segmen, dari belakang.
  for (let i = segments.length - 1; i >= 0; i--) {
    let segment = segments[i];
    if (provinceByAlias.has(segment)) continue;
    // "Jl. Bandung" / "Desa Malang": nama setelah kata depan ini bukan kab/kota
    const minStart = NON_CITY_PREFIXES.has(segment.split(" ")[0]) ? 2 : 0;
    // Buang nama provinsi yang menempel di akhir, mis. "malang jawa timur"
    for (const alias of provinceAliasesByLength) {
      if (segment.endsWith(` ${alias}`)) {
        segment = segment.slice(0, -alias.length - 1);
        break;
      }
    }
    const tail = segment.split(" ");
    for (let n = Math.min(4, tail.length - minStart); n >= 1; n--) {
      const candidates = citiesByBareName.get(compact(tail.slice(-n).join("")));
      if (!candidates) continue;
      if (candidates.length === 1) {
        return { kind: "exact", entry: candidates[0], viaKeyword: false };
      }
      return { kind: "ambiguous", candidates };
    }
  }
  return null;
}

const cache = new Map<string, Lokasi>();

export function parseLokasi(address?: string | null): Lokasi {
  if (!address || !address.trim()) return { province: null, city: null };
  const cached = cache.get(address);
  if (cached) return cached;

  const segments = segmentsOf(normalize(address));
  const { province, strong } = detectProvince(segments);
  const city = detectCity(segments);

  let result: Lokasi = { province, city: null };
  if (city?.kind === "exact") {
    const { entry, viaKeyword } = city;
    if (!province || !strong || entry.province === province || viaKeyword) {
      // Kab/kota lebih spesifik daripada nama provinsi yang ditulis
      result = { province: entry.province, city: entry.label };
    }
  } else if (city?.kind === "ambiguous" && !province) {
    const provinces = new Set(city.candidates.map((c) => c.province));
    if (provinces.size === 1) {
      result = { province: city.candidates[0].province, city: null };
    }
  }

  cache.set(address, result);
  return result;
}
